import {Vue , Component, Watch} from 'vue-property-decorator';
import { LocaleMessage } from 'vue-i18n';
import canAutoplay from 'can-autoplay';
import KEYS from '@/values/keys';
import { COURSE_EVENTS } from '@/content/values/events';
import { PRESENTATION_MODE_EVENTS } from '@/content/values/ed-presentation-mode.values';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import { DISCONNECTED_SOCKET_EVENT } from '@/core/values/app.values';

@Component({})
export class CourseMultimediaObjectMixin extends Vue {

  /* Data */
  public mediaElement: HTMLMediaElement = null;
  private mediaElementType: string = '';
  private conditionsState : boolean = false;
  private saveInterval : NodeJS.Timeout = null;
  private currentMessage = null;
  private isPlaying : boolean = false;
  private processEvents = {
    playing: this.playMediaElement,
    paused: this.pauseMediaElement,
    volume: this.volumeMediaElement
  }
  private currentData : {
    currentTime: number,
    isPaused: boolean,
    eventType: string,
    isMuted: boolean,
    volume: number
  } = {
    currentTime: 0,
    isPaused: false,
    eventType: 'pause',
    isMuted: false,
    volume: 1
  }
  private onlySaveDataRealTime : boolean = false;

  /* Computed */
  get isPresentationModeStudent(): boolean {
    return this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get isPresentationModeTeacher(): boolean {
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get initialPresentationModeMessage() : WebSocketMessageResponse {
    return this.$store.state.MeetingsStore.initialPresentationModeMessage;
  }

  get currentBoardUser(): any {
    return this.$store.state.MeetingsStore.currentBoardUser;
  }

  get currentBoardUserId(): number {
      return this.currentBoardUser?.id;
  }

  get amICurrentBoardUser(): boolean {
    return this.currentBoardUserId && this.currentBoardUserId == this.sessionUserId;
  }

  get isCurrentBoardUserTeacher(): boolean {
    return Object.keys(this.currentBoardUser).length === 0 || this.currentBoardUser.isTeacher;
  }

  get isTeacherSharing() : boolean{
    return Object.keys(this.$store.state.MeetingsStore.shareTaskData).length === 0 || this.$store.state.MeetingsStore.shareTaskData.active;
  }

  get sessionUserId(): number {
    return this.$store.state.AuthStore.user.id;
  }

  /* Methods */
  public setMediaElement(mediaElement: HTMLMediaElement , mediaElementType: string) {
    this.mediaElement = mediaElement;
    this.mediaElementType = mediaElementType;
  }

  public setTeacherListenerEvents() {
    this.addPlayEvent();
    this.addPauseEvent();
    this.addVolumeEvent();
    this.addEndedEvent();
    this.addTimeUpdateEvent();
  }

  public setStudentInitalState() {
    this.validateAutoplay();
    this.addEndedEvent();
  }

  private addPlayEvent() {
    this.mediaElement.addEventListener('play', () => {
      this.currentData.currentTime = this.mediaElement.currentTime;
      this.currentData.isPaused = false;
      this.currentData.eventType = 'playing';
      this.sendCurrentData(this.onlySaveDataRealTime);
      this.addIntervalEvent();
    }, false);
  }

  private addPauseEvent() {
    this.mediaElement.addEventListener('pause', () => {
      clearInterval(this.saveInterval);
      this.currentData.isPaused = true;
      this.currentData.eventType = 'paused';
      this.sendCurrentData(this.onlySaveDataRealTime);
    }, false);
  }

  private addVolumeEvent() {
    this.mediaElement.addEventListener('volumechange', () => {
      clearInterval(this.saveInterval);
      this.currentData.isMuted = this.mediaElement.muted;
      this.currentData.volume = this.mediaElement.volume;
      this.currentData.eventType = 'volume';
      this.sendCurrentData(this.onlySaveDataRealTime);
      this.addIntervalEvent();
    }, false);
  }

  private addEndedEvent() {
    this.mediaElement.addEventListener('ended', () => {
      this.mediaElement.pause();
      this.mediaElement.currentTime = 0;
    }, false);
  }

  private addTimeUpdateEvent() {
    this.mediaElement.addEventListener('timeupdate', () => {
      let delayTimeFrameInSeconds = 2;
      let currentTimeInSeconds = Math.floor(this.mediaElement.currentTime);
      let isTimeToNotify = currentTimeInSeconds % delayTimeFrameInSeconds === 0 && currentTimeInSeconds !== Math.floor(this.currentData.currentTime);
      let isPlaying = !this.mediaElement.paused;
      if( isPlaying && isTimeToNotify ) {
        this.currentData.currentTime = this.mediaElement.currentTime;
        this.currentData.isPaused = false;
        this.currentData.eventType = 'playing';
        this.sendCurrentData(this.onlySaveDataRealTime);  
      }
    }, false);
  }

  private addIntervalEvent() {
    clearInterval(this.saveInterval);
    this.saveInterval = setInterval(() => {
      this.currentData.currentTime = this.mediaElement.currentTime;
      this.sendCurrentData(this.onlySaveDataRealTime);
    }, 2000);
  }

  private sendCurrentData(onlySave : boolean) {
    this.$edEventBus.$emit(COURSE_EVENTS.CHANGE_MULTIMEDIA_STATE, 
    {
      onlySave, 
      data: this.currentData, 
      type: this.mediaElementType}
    );
  }

  public updateMediaElementState(data: any) {    
    if(!this.conditionsState) {            
      this.currentMessage = data;
      return;
    }
    if(this.currentMessage) data = this.currentMessage;
    if(data === null) return;
    let dataParsed = JSON.parse(data);        
    if(this.processEvents[dataParsed.eventType]) this.processEvents[dataParsed.eventType](dataParsed);
    this.currentMessage = null;
  }

  private playMediaElement(data: any) {
    if(this.isPlaying) {
      this.changeProperties(data);
      return;
    }
    this.mediaElement.play()
      .then(() => {
      this.isPlaying = true;
      this.changeProperties(data);
      })
      .catch(() => this.showPlayError(data));
  }

  private pauseMediaElement() {
    this.mediaElement.pause();
    this.isPlaying = false;
  }

  private volumeMediaElement(data: any) {
    this.mediaElement.volume = data.volume;
    this.mediaElement.muted = data.volume === 0 ? true : data.isMuted;
  }

  private changeProperties(data : any) {
    this.mediaElement.currentTime = data.currentTime;
    this.volumeMediaElement(data);
  }

  private validateInitialMessage() {
    if(this.initialPresentationModeMessage && 
      (this.initialPresentationModeMessage.messages['Audio'] || this.initialPresentationModeMessage.messages['MultimediaVideo']) ) {
        return;
    }
    this.updateMediaElementState(null);
  }

  private validateAutoplay() {
    if(this.mediaElement instanceof HTMLVideoElement) {
      let text: LocaleMessage = this.$i18n.t('meetings.presentationMode.autoplayDisabledMessage');
      canAutoplay.video()
        .then( ({result}) => this.handleAutoPlayResult(result , text));
      return;
    }
    if(this.mediaElement instanceof HTMLAudioElement) {
      let text: LocaleMessage = this.$i18n.t('meetings.presentationMode.autoplayAudioDisabledMessage');
      canAutoplay.audio()
        .then( ({result}) => this.handleAutoPlayResult(result , text));
    }
  }

  private handleAutoPlayResult(result: any , body: LocaleMessage) {
    if(!result) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('meetings.presentationMode.autoplayDisabledTitle'),
        body: body,
        type: KEYS.EVENT_TYPES.WARNING,
        action: () => {
          this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
          this.conditionsState = true;
          this.validateInitialMessage();
        }
      });
    } else {
      this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
      this.conditionsState = true;
    }
  }

  private showPlayError(data: any) {
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('meetings.presentationMode.browserSettingTitle'),
      body: this.$i18n.t('meetings.presentationMode.audioPlayError'),
      type: KEYS.EVENT_TYPES.WARNING,
      action: () => this.playMediaElement(data)
    });
  }

  mounted() {
    this.$edEventBus.$on(DISCONNECTED_SOCKET_EVENT, this.pauseMediaElement);
  }

  beforeDestroy() {
    this.$edEventBus.$off(DISCONNECTED_SOCKET_EVENT, this.pauseMediaElement);
  }


  @Watch('isTeacherSharing', { immediate: true, deep : true })
  isTeacherSharingChanged(newValue: any): void {
    this.onlySaveDataRealTime = !newValue;
  }

}